<template>
  <div>
    <div
      v-if="loading"
      class="q-pa-sm"
    >
      <QSpinner :size="30" />
    </div>
    <div
      v-else
      class="q-pa-sm"
    >
      <h4>{{ title }}</h4>
      <div
        v-for="source in availableSources"
        :key="source.field"
        class="q-mb-sm text-silver-white col-6 cursor-pointer"
        :class="'bg-' + source.field"
        @click="openFilterMentionsModal(source)"
      >
        <div class="q-pa-md">
          <span v-if="filterCount(source) > 0">
            {{ source.label }} Filters
          </span>
          <span v-else> Any {{ source.label }} </span>
          <QIcon name="arrow_drop_down" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";

import { Organisation } from "shared/resources";
import { useOrganisationFeaturesStore } from "shared/stores/organisationFeatures";

export default {
  name: "CategoryAndLocationFilters",
  props: {
    modelValue: {
      type: Object,
      required: true,
      validator(modelValue) {
        return Boolean(modelValue.filters);
      },
    },
    title: {
      type: String,
      default: "Category and Location Filters",
    },
    sourceTypes: {
      type: Array,
      default: null,
    },
    organisationId: {
      type: [Number, String],
      required: true,
    },
    showCaldFilter: {
      type: Boolean,
    },
    showInternationalFilters: {
      type: Boolean,
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const organisationFeaturesStore = useOrganisationFeaturesStore();

    const { enabledSources } = storeToRefs(organisationFeaturesStore);

    return {
      enabledSources,
    };
  },
  data() {
    return {
      hasViewNZPrintContent: false,
      hasViewNZOnlineContent: false,
      hasCALDSources: false,
      hasInternationalMaxSources: false,
      loading: true,
      organisation: {
        features: [],
      },
    };
  },
  computed: {
    stream: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    availableSources() {
      const sources = this.sourceTypes ? this.sourceTypes : this.enabledSources;

      return sources.filter((source) => source.categoryLocationFiltersEnabled);
    },
    availableSourceFields() {
      return this.availableSources.map((source) => source.field);
    },
  },
  mounted() {
    this.loadNZContentFeatures();
  },
  methods: {
    async loadOrganisationFeatures() {
      this.organisation = (
        await Organisation.selectExtra(["features"]).find(this.organisationId)
      ).data;
    },
    async loadNZContentFeatures() {
      try {
        await this.loadOrganisationFeatures();

        this.hasViewNZPrintContent = this.organisation.features.includes(
          "view_new_zealand_print_and_magazine_content"
        );

        this.hasViewNZOnlineContent = this.organisation.features.includes(
          "view_new_zealand_online_content"
        );

        this.hasCALDSources =
          this.organisation.features.includes("has_cald_sources");

        this.hasInternationalMaxSources = this.organisation.features.includes(
          "has_international_max"
        );
      } catch (error) {
        this.$q.notify({
          message: `${error} - an error occured whilst loading the organisations features`,
          color: "red",
        });
      } finally {
        this.loading = false;
      }
    },
    showInternationalMaxFilter(type) {
      return (
        this.showInternationalFilters &&
        this.hasInternationalMaxSources &&
        type.field === "print"
      );
    },
    showCALDFilter(type) {
      return (
        this.showCaldFilter &&
        this.hasCALDSources &&
        (type.field === "online" || type.field === "print")
      );
    },
    openFilterMentionsModal(type) {
      this.currentFilter = type;

      this.$modals.open("FilterMentionsModal", {
        props: {
          stream: this.stream,
          source: this.currentFilter,
          allowedSources: this.availableSourceFields,
          showTierFilters: true,
          isAdmin: true,
          organisation: this.organisation,
          showCALDFilter: this.showCALDFilter(type),
          showInternationalMaxFilter: this.showInternationalMaxFilter(type),
        },
        events: {
          refresh: (updatedStream) => {
            this.stream = updatedStream;
          },
          "update:filters": (filters) => {
            this.stream.filters = filters;
          },
          "update:cald": ({ source, value }) => {
            this.stream[`include_${source}_cald_sources`] = value;
          },
          "update:tier": ({ source, value }) => {
            this.stream[`${source}_tier`] = value;
          },
        },
      });
    },
    filterCount(source) {
      const excludeNZPrintContentFilter = this.stream.filters.find(
        (streamFilter) =>
          streamFilter.name === "New Zealand" &&
          streamFilter.scope === "print" &&
          streamFilter.target_type === "Location"
      );

      const excludeNZOnlineContentFilter = this.stream.filters.find(
        (streamFilter) =>
          streamFilter.name === "New Zealand" &&
          streamFilter.scope === "online" &&
          streamFilter.target_type === "Location"
      );

      const filters = this.stream.filters.filter(
        (streamFilter) =>
          streamFilter.scope === source.field &&
          streamFilter.target_type !== "SourceGroup"
      );

      let filtersCount = filters.length;

      if (
        Boolean(excludeNZPrintContentFilter) &&
        !this.hasViewNZPrintContent &&
        source.label === "Print"
      ) {
        filtersCount -= 1;
      }

      if (
        Boolean(excludeNZOnlineContentFilter) &&
        !this.hasViewNZOnlineContent &&
        source.label === "Online"
      ) {
        filtersCount -= 1;
      }

      return filtersCount;
    },
  },
};
</script>
